import type { PaymentSheet } from '../../components/paymentSheet';
import type { VendorPaymentMethodElement } from '../../types/component';
import type {
  SalesforcePaymentsCheckoutActions,
  SalesforcePaymentsCheckoutConfig,
  SalesforcePaymentsSetupConfig,
  SavedPaymentMethod,
} from '../../types/config';
import { PaymentMethodElement, PaymentMethodType, Vendor } from '../../types/paymentMethodSet';
import type { PaymentMode } from '../../types/paymentMethodSet';
import type { BillingDetails, PaymentIntentRequest, ShippingDetails } from '../../types/paymentRequest';
import type { AdyenPaymentData, SetupResponse } from '../../types/paymentResponse';
import { type PaymentResponse, ResponseCode } from '../../types/paymentResponse';
import type { MetadataReader } from '../../util/metadata-reader';
import { getLabelsByCardType, label } from '../../util/utility';
import type { SfPaymentMethodComponentConstructor } from '../sfp-payment-method-component';
import type {
  ISavedPaymentMethodComponent,
  SfpSavedPaymentMethodComponent,
} from '../sfp-saved-payment-method-component';
import { SfpAdyenWeb } from './sfp-adyen-web';

export class SfpAdyenSavedPaymentMethodComponent extends SfpAdyenWeb implements ISavedPaymentMethodComponent {
  TYPE: PaymentMethodType;
  ELEMENT = PaymentMethodElement.ADYEN_SAVABLE_PAYMENT_METHOD;

  config: SalesforcePaymentsCheckoutConfig | SalesforcePaymentsSetupConfig;

  webComponentElement: HTMLElement;

  webComponent: any;

  billingDetails: BillingDetails;
  shippingDetails: ShippingDetails;
  paymentResolve: any;
  paymentIntentResponse: any;
  metadata: MetadataReader;
  isSavedPaymentMethodsApplicable: boolean;
  makePaymentMethodDefault: boolean;
  savePaymentMethodFuture: boolean;
  savedPaymentMethod: SavedPaymentMethod;
  createSetupIntent: any;
  setupIntentResponse: any;

  initializeSavedPaymentMethod(savedPaymentMethod: SavedPaymentMethod): void {
    this.TYPE = PaymentMethodType[(savedPaymentMethod.type || '').toUpperCase() as keyof typeof PaymentMethodType];
    this.savedPaymentMethod = savedPaymentMethod;
  }

  public getTitle(): string {
    if (this.TYPE === PaymentMethodType.CARD) {
      return getLabelsByCardType(this.getSubType(), this.config.labels).label;
    } else if (this.TYPE === PaymentMethodType.US_BANK_ACCOUNT) {
      if (this.savedPaymentMethod && this.savedPaymentMethod.bankName) {
        const paymentMethodTitle: string = label(this.config.labels, 'usBankAccountPaymentMethodTitle');
        return paymentMethodTitle.replace('{0}', this.savedPaymentMethod.bankName);
      }
      return label(this.config.labels, 'usBankAccountTitle');
    } else if (this.TYPE === PaymentMethodType.SEPA_DEBIT) {
      return label(this.config.labels, 'sepaDebitTitle');
    } else if (this.TYPE === PaymentMethodType.BACS_DEBIT) {
      return label(this.config.labels, 'bacsDebitTitle');
    }
    return '';
  }

  public getIconSrc(): string {
    if (this.TYPE === PaymentMethodType.CARD) {
      return this.metadataReader.getVariantPaymentMethodIconUrl(
        Vendor.ADYEN,
        this.getSubType(),
        'Card',
        this.config.theme?.mode
      );
    }
    return this.metadataReader.getPaymentMethodIconUrl(this.TYPE, this.config.theme?.mode);
  }

  public getIconTitle(): string {
    if (this.TYPE === PaymentMethodType.CARD) {
      return getLabelsByCardType(this.getSubType(), this.config.labels).iconTitle;
    } else if (this.TYPE === PaymentMethodType.US_BANK_ACCOUNT) {
      return label(this.config.labels, 'usBankAccountIconTitle');
    } else if (this.TYPE === PaymentMethodType.SEPA_DEBIT) {
      return label(this.config.labels, 'sepaDebitIconTitle');
    } else if (this.TYPE === PaymentMethodType.BACS_DEBIT) {
      return label(this.config.labels, 'bacsDebitIconTitle');
    }
    return '';
  }

  public getDetails(): string {
    return label(this.config.labels, 'savedPaymentMethodEndingWith').replace(
      '{0}',
      this.savedPaymentMethod?.last4 || ''
    );
  }

  public getSubType(): string {
    return this.savedPaymentMethod?.network || 'unknown';
  }

  public setMakePaymentMethodDefault(makePaymentMethodDefault: boolean): void {
    this.makePaymentMethodDefault = makePaymentMethodDefault;
  }

  public setSavePaymentMethodFuture(savePaymentMethodFuture: boolean): void {
    this.savePaymentMethodFuture = savePaymentMethodFuture;
  }

  asSavedPaymentMethodComponent(): SfpSavedPaymentMethodComponent {
    return this as SfpSavedPaymentMethodComponent;
  }

  hasError(): boolean {
    if (this.savedPaymentMethod) {
      return false;
    }
    return super.hasError();
  }

  async handleConfirm(
    _createIntent: Function,
    billingDetails: BillingDetails,
    shippingDetails: ShippingDetails
  ): Promise<PaymentResponse> {
    if (this.savedPaymentMethod) {
      // If using SPM, make use of data stored with the SPM and return that.
      return await this.getSpmResponseData(billingDetails, shippingDetails);
    }
    // In case of not using SPM, fallback to web component handleConfirm function.
    return super.handleConfirm(_createIntent, billingDetails, shippingDetails);
  }

  async getSpmResponseData(billingDetails: BillingDetails, shippingDetails: ShippingDetails): Promise<PaymentResponse> {
    const paymentData = {
      paymentMethod: {
        type: this.metadataReader.getGatewayPaymentMethodMapping(this.TYPE, Vendor.ADYEN),
        storedPaymentMethodId: this.savedPaymentMethod.gatewayTokenId,
      },
      returnUrl: this.config.options.returnUrl,
      shippingDetails: shippingDetails,
      billingDetails: billingDetails,
      origin: window.location.origin,
      browserInfo: {
        acceptHeader: '*/*',
        colorDepth: window.screen.colorDepth,
        language: window.navigator.language,
        javaEnabled: window.navigator.javaEnabled(),
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        userAgent: window.navigator.userAgent,
        timeZoneOffset: new Date().getTimezoneOffset(),
      },
    };

    // Deep clone the paymentData object to make sure it is mutable because create intent function may execute functions on it
    const paymentDataCopy = JSON.parse(JSON.stringify(paymentData));

    // This makes call to payment-intent api, which can cause unexpected exception if data is incorrect at adyen's end.
    // We need to handle that gracefully.
    try {
      this.paymentIntentResponse = await (this.config.actions as SalesforcePaymentsCheckoutActions).createIntent(
        paymentDataCopy
      );
    } catch (err) {
      return {
        responseCode: ResponseCode.FAILURE,
        data: {
          error: 'Payment Failed',
          status: err.status,
        },
      };
    }

    let responseData;
    // No response from payment intent means pre auth failed for the payment.
    if (this.paymentIntentResponse) {
      const responsePaymentData = {
        id: this.paymentIntentResponse.pspReference
          ? this.paymentIntentResponse.pspReference
          : this.paymentIntentResponse.guid,
        uuid: this.paymentIntentResponse.guid,
        paymentGatewayId: this.savedPaymentMethod.gatewayId,
        type:
          this.TYPE === PaymentMethodType.CARD && this.savedPaymentMethod.network
            ? this.savedPaymentMethod.network
            : this.TYPE,
        isManualCapture: this.config.options.useManualCapture ? true : false,
      };
      responseData = {
        responseCode: ResponseCode.SUCCESS,
        data: {
          paymentData: responsePaymentData as AdyenPaymentData,
          paymentToken: this.paymentIntentResponse.guid,
          billingDetails: billingDetails,
        },
      };
    } else {
      responseData = {
        responseCode: ResponseCode.FAILURE,
        data: {
          error: 'Payment Failed',
        },
      };
    }
    return responseData;
  }

  async handleConfirmSetup(createSetupIntent: Function, billingDetails: BillingDetails): Promise<SetupResponse> {
    this.createSetupIntent = createSetupIntent;
    this.billingDetails = billingDetails;
    this.isAddSpm = true;
    const promise: Promise<any> = new Promise((resolve) => {
      this.paymentResolve = resolve;
    });
    if (this.hasError()) {
      const response = {
        responseCode: ResponseCode.FAILURE,
        data: {},
      };
      this.paymentResolve(response);
    } else {
      await this.webComponent.submit();
    }
    return promise;
  }

  async handleSetupIntent(data: any): Promise<void> {
    const paymentData: { paymentMethod?: string } = {
      paymentMethod: data.paymentMethod,
    };
    try {
      this.setupIntentResponse = await this.createSetupIntent(paymentData);
      let response: SetupResponse;
      if (this.setupIntentResponse.resultCode === 'Authorised') {
        response = {
          responseCode: ResponseCode.SUCCESS,
          data: {
            status: 'async',
          },
        };
      } else {
        response = {
          responseCode: ResponseCode.FAILURE,
          data: {},
        };
        this.updateWebComponent();
      }
      this.paymentResolve(response);
    } catch (err) {
      this.paymentResolve({
        responseCode: ResponseCode.FAILURE,
        data: {
          error: 'Payment Failed',
          status: err.status,
        },
      });
      this.updateWebComponent();
    }
  }
}

export const createSfpAdyenSavedPaymentMethodComponent: SfPaymentMethodComponentConstructor = function (
  parentComponent: PaymentSheet,
  vendorObject: any,
  elements: any,
  metadataReader: MetadataReader,
  config: SalesforcePaymentsCheckoutConfig | SalesforcePaymentsSetupConfig,
  paymentRequest: PaymentIntentRequest,
  paymentMethod: VendorPaymentMethodElement,
  paymentMode?: PaymentMode
) {
  return new SfpAdyenSavedPaymentMethodComponent(
    parentComponent,
    vendorObject,
    elements,
    metadataReader,
    config,
    paymentRequest,
    paymentMethod,
    paymentMode
  );
};
